/**
 * Phase 26 Part 2 — fleet-tier access gate.
 *
 * Renders when a free-tier user attempts to:
 *   - Switch to Fleet mode via the top-nav toggle
 *   - Navigate to a fleet scenario (caller opens the gate)
 *   - Click "Compute Fleet TCO"
 *
 * Single rendering site in Layout so any caller that flips
 * `useAppModeStore.fleetGateOpen` gets the same UI. Successful redemption
 * upgrades the tier in-store, closes the modal, and lets the caller proceed.
 */
import { useEffect, useState } from 'react';
import { useAppModeStore } from '../store/useAppModeStore';
import { useAuthStore } from '../store/useAuthStore';
import { redeemPromoCode } from '../utils/api';

interface FleetGateModalProps {
  /** Called after a successful redemption so callers can complete their flow
   * (e.g. flip activeMode to 'fleet' after the user originally attempted it). */
  onRedeemed?: () => void;
}

export default function FleetGateModal({ onRedeemed }: FleetGateModalProps) {
  const fleetGateOpen = useAppModeStore((s) => s.fleetGateOpen);
  const setFleetGateOpen = useAppModeStore((s) => s.setFleetGateOpen);
  const initTier = useAppModeStore((s) => s.initTier);
  const setMode = useAppModeStore((s) => s.setMode);
  const initialize = useAppModeStore((s) => s.initialize);
  // UX audit fix: when the profile load failed the tier is UNKNOWN, not free.
  // In that case we must not present this as a paywall/upsell — show a retry.
  const profileError = useAppModeStore((s) => s.profileError);
  const { user } = useAuthStore();

  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    if (fleetGateOpen) {
      setCode('');
      setError(null);
      setSuccess(false);
      setRetrying(false);
    }
  }, [fleetGateOpen]);

  if (!fleetGateOpen) return null;

  const close = () => {
    if (submitting || retrying) return;
    setFleetGateOpen(false);
  };

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await initialize(!!user);
    } finally {
      setRetrying(false);
      // initialize() resolves the tier; close the modal so the user can re-try
      // the action with the now-known plan (fleet flips through cleanly if the
      // load showed they have access, or the real upsell shows if they don't).
      setFleetGateOpen(false);
    }
  };

  const handleRedeem = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) {
      setError('Enter a code to continue.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await redeemPromoCode(trimmed);
      initTier(res.tier);
      setSuccess(true);
      // Short pause so the user sees the success state before we flip modes.
      setTimeout(async () => {
        setFleetGateOpen(false);
        // Auto-switch to fleet mode; the gate is now bypassed because tier
        // is no longer 'free'.
        await setMode('fleet', !!user);
        onRedeemed?.();
      }, 700);
    } catch (err) {
      setError(
        err instanceof Error && err.message
          ? err.message
          : 'Invalid or expired code.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 px-4"
      onClick={close}
      role="dialog"
      aria-modal="true"
      aria-labelledby="fleet-gate-title"
    >
      <div
        className="cnc-panel rounded-xl p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        {profileError ? (
          // We couldn't load the user's plan — do NOT render the paywall/upsell
          // as if they're definitively free. Show an error + retry instead.
          <>
            <h2
              id="fleet-gate-title"
              className="text-xl font-semibold mb-2"
              style={{ color: 'var(--text-primary, #e2e8f0)' }}
            >
              Couldn't verify your plan
            </h2>
            <p
              className="text-sm mb-4"
              style={{ color: 'var(--text-secondary, #a0aec0)' }}
            >
              We couldn't reach the server to check your access. It may be
              waking up (about a minute). Please retry.
            </p>
            <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2 mt-2">
              <button
                type="button"
                onClick={close}
                disabled={retrying}
                className="btn-secondary px-4 py-2 text-sm rounded-lg"
              >
                Continue in Personal mode
              </button>
              <button
                type="button"
                onClick={handleRetry}
                disabled={retrying}
                className="btn-primary px-4 py-2 text-sm rounded-lg"
              >
                {retrying ? 'Retrying…' : 'Retry'}
              </button>
            </div>
          </>
        ) : (
        <>
        <h2
          id="fleet-gate-title"
          className="text-xl font-semibold mb-2"
          style={{ color: 'var(--text-primary, #e2e8f0)' }}
        >
          Fleet access required
        </h2>
        <p
          className="text-sm mb-4"
          style={{ color: 'var(--text-secondary, #a0aec0)' }}
        >
          Fleet TCO is available to subscribers. Enter a promo code for beta
          access, or check back when paid plans launch.
        </p>

        <form onSubmit={handleRedeem}>
          <label
            htmlFor="promo-code-input"
            className="block text-xs uppercase tracking-wide mb-1"
            style={{ color: 'var(--text-secondary, #a0aec0)' }}
          >
            Promo code
          </label>
          <input
            id="promo-code-input"
            type="text"
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={submitting || success}
            className="w-full rounded-lg px-3 py-2 mb-3 cnc-input"
            style={{
              background: 'var(--input-bg, rgba(255,255,255,0.05))',
              color: 'var(--text-primary, #e2e8f0)',
              border: '1px solid var(--input-border, rgba(255,255,255,0.1))',
            }}
            placeholder="Enter promo code"
            aria-invalid={!!error}
            aria-describedby={error ? 'promo-code-error' : undefined}
          />

          {error && (
            <p
              id="promo-code-error"
              className="text-sm mb-3"
              style={{ color: '#f87171' }}
              role="alert"
            >
              {error}
            </p>
          )}
          {success && (
            <p
              className="text-sm mb-3"
              style={{ color: '#4ade80' }}
              role="status"
            >
              Access granted. Welcome to Fleet TCO.
            </p>
          )}

          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2 mt-2">
            <button
              type="button"
              onClick={close}
              disabled={submitting}
              className="btn-secondary px-4 py-2 text-sm rounded-lg"
            >
              Continue in Personal mode
            </button>
            <button
              type="submit"
              disabled={submitting || success}
              className="btn-primary px-4 py-2 text-sm rounded-lg"
            >
              {submitting ? 'Redeeming…' : 'Redeem code'}
            </button>
          </div>
        </form>

        <p
          className="text-xs mt-4"
          style={{ color: 'var(--text-secondary, #a0aec0)' }}
        >
          Paid plans coming soon.
        </p>
        </>
        )}
      </div>
    </div>
  );
}
